import React, { useState, useEffect } from 'react';
import { getState, setState as setGlobalState, subscribe } from '../lib/userStore';
import AIAssistant from '../lib/components/AIAssistant';

// ─── Screening logic ──────────────────────────────────────────────────────────
function getScreenings(age, gender) {
  const n = parseInt(age, 10);
  const g = (gender || '').toLowerCase();
  const results = [];

  results.push({ label: 'Blood pressure check', detail: 'Annually for all adults' });

  if (!isNaN(n)) {
    if (n >= 18)  results.push({ label: 'Cholesterol screening', detail: 'Every 5 years starting at 18' });
    if (n >= 35)  results.push({ label: 'Diabetes screening', detail: 'Every 3 years starting at 35' });
    if (n >= 45)  results.push({ label: 'Colorectal cancer screening', detail: 'Starting at 45 — colonoscopy every 10 years' });
    if (g === 'female' && n >= 40) results.push({ label: 'Mammogram', detail: 'Annually starting at 40' });
    if (g === 'female' && n >= 21) results.push({ label: 'Cervical cancer screening (Pap)', detail: 'Every 3 years starting at 21' });
    results.push({ label: 'Skin cancer check', detail: 'Annually — especially if outdoors often' });
  } else {
    results.push({ label: 'Cholesterol screening', detail: 'Every 5 years starting at 18' });
    results.push({ label: 'Colorectal cancer screening', detail: 'Starting at 45 — colonoscopy every 10 years' });
    results.push({ label: 'Skin cancer check', detail: 'Annually for everyone' });
  }

  return results;
}

// ─── Vaccine logic ────────────────────────────────────────────────────────────
function getVaccines(age) {
  const n = parseInt(age, 10);
  const results = [];
  results.push({ label: 'Flu vaccine', detail: 'Annually — best protection when updated each year' });
  results.push({ label: 'COVID-19', detail: 'Stay up to date with current CDC recommendations' });
  if (!isNaN(n)) {
    if (n < 26) results.push({ label: 'HPV vaccine', detail: '2–3 dose series recommended through age 26' });
    if (n >= 50) results.push({ label: 'Shingles (Shingrix)', detail: '2-dose series starting at age 50' });
    if (n >= 65) results.push({ label: 'Pneumococcal vaccine', detail: 'Protects against pneumonia and meningitis' });
  }
  return results;
}

const RESOURCES = [
  {
    name: 'Prisma Health Wellness Programs',
    detail: 'Free health screenings, chronic disease management, and wellness classes for Greenville residents.',
    phone: '(864) 455-7000',
    type: 'Wellness',
  },
  {
    name: 'Bon Secours Community Health Events',
    detail: 'Seasonal free screening events including blood pressure, glucose, and cholesterol checks across Upstate SC.',
    phone: '(864) 235-5271',
    type: 'Screenings',
  },
  {
    name: 'SC DHEC Immunization Clinics',
    detail: 'Walk-in vaccination clinics for flu, COVID-19, Tdap, shingles, and other routine vaccines.',
    phone: '(864) 282-4100',
    type: 'Vaccines',
  },
  {
    name: 'Greenville Free Medical Clinic',
    detail: 'Free preventative care and annual wellness visits for uninsured and underinsured adults.',
    phone: '(864) 235-6600',
    type: 'Free care',
  },
];

export default function PreventativeScreen({ navigate, goBack, user, saveProfile }) {
  const saved = getState();
  const [st, setSt] = useState({ age: saved.age, gender: saved.gender, firstName: saved.firstName });
  const [lastVisit, setLastVisit] = useState(saved.lastWellnessVisit || '');
  const [visitLogged, setVisitLogged] = useState(!!(saved.lastWellnessVisit));

  useEffect(() => subscribe(s => setSt({ age: s.age, gender: s.gender, firstName: s.firstName })), []);

  const { age, gender } = st;
  const screenings = getScreenings(age, gender);
  const vaccines   = getVaccines(age);
  const noProfile  = !age && !gender;

  async function handleLogVisit() {
    setVisitLogged(true);
    setGlobalState({ lastWellnessVisit: lastVisit });
    if (user && saveProfile) await saveProfile({ lastWellnessVisit: lastVisit });
  }

  const nextVisitYear = lastVisit ? parseInt(lastVisit.split('-')[0], 10) + 1 : null;
  const currentYear = new Date().getFullYear();
  const visitDue = nextVisitYear ? nextVisitYear <= currentYear : true;

  return (
    <div style={s.screen}>
      {/* Header */}
      <div style={s.header}>
        <div style={{ ...s.accentBar, background: 'linear-gradient(90deg, var(--teal), var(--teal-2))' }} />
        <button style={s.back} onClick={goBack}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 4L6 8l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back
        </button>
        <div style={s.title}>Stay ahead of<br /><em style={{ fontStyle: 'italic', color: 'var(--teal)' }}>your health.</em></div>
        <div style={s.subtitle}>Screenings and reminders based on your age and health profile.</div>
      </div>

      <div style={s.body}>
        <div style={s.bodyInner}>

          {/* Profile nudge if no age/gender set */}
          {noProfile && (
            <div style={s.profileNudge}>
              <div style={s.profileNudgeText}>Add your age and gender in your profile to get personalized screening reminders.</div>
              <button style={s.profileNudgeBtn} onClick={() => navigate('profile')}>Go to Profile →</button>
            </div>
          )}

          {/* Screening reminders */}
          <div style={s.sectionLabel}>Recommended screenings</div>
          <div style={s.card}>
            {screenings.map((item, i) => (
              <div key={item.label} style={{ ...s.screeningRow, borderBottom: i < screenings.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div style={s.screeningDot} />
                <div style={s.screeningText}>
                  <div style={s.screeningLabel}>{item.label}</div>
                  <div style={s.screeningDetail}>{item.detail}</div>
                </div>
              </div>
            ))}
          </div>

          {/* AI assistant */}
          <div style={s.sectionLabel}>Ask about your health</div>
          <AIAssistant
            placeholder="Ask about screenings, vaccines, or what's covered…"
            starterPrompts={[
              parseInt(age, 10) >= 45 ? 'When do I need a colonoscopy?' : 'What screenings should I prioritize this year?',
              'Are wellness visits covered under my plan?',
              'Where can I get a flu shot in Greenville?',
              'What happens at an annual wellness visit?',
            ]}
          />

          {/* Vaccines */}
          <div style={s.sectionLabel}>Vaccines to stay current on</div>
          <div style={s.card}>
            {vaccines.map((item, i) => (
              <div key={item.label} style={{ ...s.screeningRow, borderBottom: i < vaccines.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ ...s.screeningDot, background: 'var(--amber)' }} />
                <div style={s.screeningText}>
                  <div style={s.screeningLabel}>{item.label}</div>
                  <div style={s.screeningDetail}>{item.detail}</div>
                </div>
              </div>
            ))}
            <div style={s.vaccineFooter}>
              <a href="tel:8642824100" style={s.vaccineLink}>SC DHEC walk-in clinics — (864) 282-4100 →</a>
            </div>
          </div>

          {/* Annual wellness visit tracker */}
          <div style={s.sectionLabel}>Annual wellness visit</div>
          <div style={s.card}>
            <div style={s.trackerInner}>
              <div style={s.trackerHeader}>
                <div>
                  <div style={s.trackerTitle}>Last wellness visit</div>
                  <div style={s.trackerSub}>Your annual check-up with a primary care provider</div>
                </div>
                {visitDue && <div style={s.dueBadge}>Due {nextVisitYear ? `in ${nextVisitYear}` : 'now'}</div>}
              </div>
              {visitLogged ? (
                <div style={s.visitLogged}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8l3.5 3.5 6.5-7" stroke="var(--teal)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Logged: {new Date(lastVisit + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  <button style={s.editVisitBtn} onClick={() => setVisitLogged(false)}>Edit</button>
                </div>
              ) : (
                <div style={s.visitForm}>
                  <div style={s.fieldLabel}>When was your last visit?</div>
                  <input
                    type="month"
                    style={s.input}
                    value={lastVisit}
                    onChange={e => setLastVisit(e.target.value)}
                    max={new Date().toISOString().slice(0, 7)}
                  />
                  <button
                    style={{ ...s.logBtn, opacity: lastVisit ? 1 : 0.5 }}
                    disabled={!lastVisit}
                    onClick={handleLogVisit}
                  >
                    Log visit
                  </button>
                </div>
              )}
              {!lastVisit && !visitLogged && (
                <div style={s.findVisitLink}>
                  <span style={{ color: 'var(--ink-4)', fontSize: '12.5px' }}>Need a wellness visit? </span>
                  <button style={s.linkBtn} onClick={() => navigate('findCare', { providerContext: { careType: 'primary', resultLevel: 'soon' } })}>Find a provider →</button>
                </div>
              )}
            </div>
          </div>

          {/* Greenville preventative resources */}
          <div style={s.sectionLabel}>Greenville resources</div>
          {RESOURCES.map(r => (
            <div key={r.name} style={s.resourceCard}>
              <div style={s.resourceHeader}>
                <div style={s.resourceBadge}>{r.type}</div>
                <div style={s.resourceName}>{r.name}</div>
              </div>
              <div style={s.resourceDetail}>{r.detail}</div>
              <a href={`tel:${r.phone.replace(/\D/g, '')}`} style={s.resourcePhone}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                  <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" fill="var(--teal)"/>
                </svg>
                {r.phone}
              </a>
            </div>
          ))}

          <div style={{ height: '8px' }} />
        </div>
      </div>
    </div>
  );
}

const s = {
  screen: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--cream)' },
  header: { padding: '52px 24px 20px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', position: 'relative', flexShrink: 0 },
  accentBar: { position: 'absolute', top: 0, left: 0, right: 0, height: '3px' },
  back: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 500, color: 'var(--ink-3)', cursor: 'pointer', marginBottom: '16px', background: 'none', border: 'none', padding: 0, fontFamily: 'var(--font-body)' },
  title: { fontFamily: 'var(--font-display)', fontWeight: 400, fontSize: '26px', color: 'var(--ink)', letterSpacing: '-0.5px', lineHeight: 1.2 },
  subtitle: { fontSize: '13.5px', color: 'var(--ink-3)', marginTop: '6px', lineHeight: 1.5 },
  body: { flex: 1, minHeight: 0, overflowY: 'auto', msOverflowStyle: 'none', scrollbarWidth: 'none' },
  bodyInner: { display: 'flex', flexDirection: 'column', gap: '12px', padding: '20px' },
  sectionLabel: { fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-4)', marginBottom: '-4px' },
  profileNudge: { background: 'var(--teal-faint)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(30,92,84,0.15)', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' },
  profileNudgeText: { fontSize: '13.5px', color: 'var(--ink-2)', lineHeight: 1.5 },
  profileNudgeBtn: { alignSelf: 'flex-start', background: 'none', border: 'none', color: 'var(--teal)', fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: 600, cursor: 'pointer', padding: 0 },
  card: { background: 'var(--surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', overflow: 'hidden' },
  screeningRow: { padding: '14px 18px', display: 'flex', alignItems: 'flex-start', gap: '12px' },
  screeningDot: { width: '8px', height: '8px', borderRadius: '50%', marginTop: '5px', flexShrink: 0, background: 'var(--teal)' },
  screeningText: { flex: 1 },
  screeningLabel: { fontSize: '13.5px', fontWeight: 500, color: 'var(--ink)', marginBottom: '2px' },
  screeningDetail: { fontSize: '12px', color: 'var(--ink-4)', lineHeight: 1.4 },
  vaccineFooter: { padding: '10px 18px', borderTop: '1px solid var(--border)' },
  vaccineLink: { fontSize: '12px', fontWeight: 600, color: 'var(--teal)', textDecoration: 'none' },
  dueBadge: { fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--teal)', background: 'var(--teal-faint)', padding: '3px 8px', borderRadius: '100px', flexShrink: 0 },
  trackerInner: { padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '12px' },
  trackerHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' },
  trackerTitle: { fontSize: '13.5px', fontWeight: 500, color: 'var(--ink)', marginBottom: '2px' },
  trackerSub: { fontSize: '12px', color: 'var(--ink-4)', lineHeight: 1.4 },
  visitLogged: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13.5px', color: 'var(--teal)', fontWeight: 500 },
  editVisitBtn: { marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--ink-4)', fontSize: '12px', fontFamily: 'var(--font-body)', cursor: 'pointer', padding: 0 },
  visitForm: { display: 'flex', flexDirection: 'column', gap: '8px' },
  fieldLabel: { fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-3)' },
  input: { width: '100%', padding: '11px 14px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--border-strong)', background: 'var(--cream)', fontFamily: 'var(--font-body)', fontSize: '14px', color: 'var(--ink)', outline: 'none', boxSizing: 'border-box' },
  logBtn: { padding: '11px 18px', background: 'var(--teal)', color: 'white', border: 'none', borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: 600, cursor: 'pointer' },
  findVisitLink: { display: 'flex', alignItems: 'center', gap: '4px' },
  linkBtn: { background: 'none', border: 'none', color: 'var(--teal)', fontFamily: 'var(--font-body)', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', padding: 0 },
  resourceCard: { background: 'var(--surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', overflow: 'hidden', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '8px' },
  resourceHeader: { display: 'flex', flexDirection: 'column', gap: '4px' },
  resourceBadge: { fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--teal)' },
  resourceName: { fontFamily: 'var(--font-display)', fontWeight: 400, fontSize: '15px', color: 'var(--ink)', letterSpacing: '-0.2px' },
  resourceDetail: { fontSize: '12.5px', color: 'var(--ink-3)', lineHeight: 1.5 },
  resourcePhone: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', color: 'var(--teal)', fontWeight: 600, textDecoration: 'none' },
};
